import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { adCampaignService } from '../../services/adCampaignService';
import { AdDisplayItem } from '../../types/adCampaign';
import { getMediaUrl } from '../../utils/mediaUrl';
import { HLSVideoPlayer } from '../social/HLSVideoPlayer';
import { AD_POOL_LIMIT, AD_ROTATION_SECONDS } from './constants';

interface BannerDisplayProps {
  placement: string; // This corresponds to the slot_slug
  width?: string | number;
  height?: string | number;
  rotationInterval?: number; // in seconds, default 10
  limit?: number;
  isVisible?: boolean;
  autoRotate?: boolean;
  advanceSignal?: number;
  ads?: AdDisplayItem[];
  loading?: boolean;
  onAdsLoaded?: (placement: string, count: number) => void;
}

const BannerDisplay: React.FC<BannerDisplayProps> = ({
  placement,
  width = '100%',
  height = 'auto',
  rotationInterval = AD_ROTATION_SECONDS,
  limit = AD_POOL_LIMIT,
  isVisible = true,
  autoRotate = true,
  advanceSignal,
  ads: controlledAds,
  loading: controlledLoading,
  onAdsLoaded,
}) => {
  const navigate = useNavigate();
  const isControlled = controlledAds !== undefined;
  const [fetchedAds, setFetchedAds] = useState<AdDisplayItem[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [fetchLoading, setFetchLoading] = useState(!isControlled);
  const [error, setError] = useState<string | null>(null);
  const ads = controlledAds ?? fetchedAds;
  const loading = controlledLoading ?? (isControlled ? false : fetchLoading);

  // Track which ads have already been impressed in this session
  const impressedAds = useRef<Set<number>>(new Set());
  const lastAdvanceSignal = useRef(advanceSignal);

  const preloadAdMedia = useCallback((ad?: AdDisplayItem) => {
    if (!ad) return;

    const src = ad.campaign_type === 'video' ? ad.thumbnail_url : ad.image_url;
    const mediaUrl = getMediaUrl(src);

    if (!mediaUrl) return;

    const image = new Image();
    image.src = mediaUrl;
  }, []);

  // Fetch ads for this placement slot
  useEffect(() => {
    if (isControlled) return;

    let isMounted = true;

    const fetchAds = async () => {
      try {
        setFetchLoading(true);
        // Map old banner placement keys to new slot slugs if necessary
        // In LeftSidebar/RightSidebar, they use sidebar_top, sidebar_middle, sidebar_bottom
        const response = await adCampaignService.getAdsForSlot(placement, limit);

        if (!isMounted) return;

        setFetchedAds(response.ads);
        setCurrentIndex(0);
        setError(null);
        onAdsLoaded?.(placement, response.ads.length);
      } catch (err: any) {
        console.error('Failed to fetch ads:', err);

        if (isMounted) {
          setError(err.message);
          onAdsLoaded?.(placement, 0);
        }
      } finally {
        if (isMounted) {
          setFetchLoading(false);
        }
      }
    };

    fetchAds();

    return () => {
      isMounted = false;
    };
  }, [isControlled, placement, limit, onAdsLoaded]);

  useEffect(() => {
    if (!isControlled) return;

    setCurrentIndex(0);
    setError(null);
    onAdsLoaded?.(placement, controlledAds?.length ?? 0);
  }, [controlledAds, isControlled, onAdsLoaded, placement]);

  useEffect(() => {
    if (!isVisible || ads.length === 0) return;

    const safeIndex = Math.min(currentIndex, ads.length - 1);
    preloadAdMedia(ads[safeIndex]);
    preloadAdMedia(ads[(safeIndex + 1) % ads.length]);
  }, [ads, currentIndex, isVisible, preloadAdMedia]);

  // Track impression when ad is displayed (only once per ad per session)
  useEffect(() => {
    if (isVisible && ads.length > 0 && !loading) {
      const currentAd = ads[Math.min(currentIndex, ads.length - 1)];
      if (!currentAd) return;

      // Only track if we haven't already tracked this ad
      if (!impressedAds.current.has(currentAd.campaign_id)) {
        impressedAds.current.add(currentAd.campaign_id);
        adCampaignService.trackView(currentAd.campaign_id).catch((err) => {
          console.error('Failed to track impression:', err);
        });
      }
    }
  }, [ads, currentIndex, loading, isVisible]);

  // Rotate ads at specified interval
  useEffect(() => {
    if (!autoRotate || !isVisible || ads.length <= 1) return;

    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % ads.length);
    }, rotationInterval * 1000);

    return () => clearInterval(interval);
  }, [ads.length, rotationInterval, autoRotate, isVisible]);

  useEffect(() => {
    if (advanceSignal === undefined) return;

    if (lastAdvanceSignal.current === undefined) {
      lastAdvanceSignal.current = advanceSignal;
      return;
    }

    if (advanceSignal !== lastAdvanceSignal.current && ads.length > 1) {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % ads.length);
    }

    lastAdvanceSignal.current = advanceSignal;
  }, [advanceSignal, ads.length]);

  // Handle ad click
  const handleClick = useCallback(async (e: React.MouseEvent) => {
    if (ads.length > 0) {
      const currentAd = ads[Math.min(currentIndex, ads.length - 1)];
      if (!currentAd) return;
      try {
        await adCampaignService.trackClick(currentAd.campaign_id);
      } catch (err) {
        console.error('Failed to track click:', err);
      }

      // Handle internal vs external links
      const clickUrl = currentAd.click_url;
      if (clickUrl) {
        if (clickUrl.startsWith('/')) {
          // Internal link - use React Router
          e.preventDefault();
          navigate(clickUrl);
        }
        // External links will follow the href naturally
      }
    }
  }, [ads, currentIndex, navigate]);

  const frameStyle = {
    width: typeof width === 'number' ? `${width}px` : width,
    height: height === 'auto' ? 'auto' : (typeof height === 'number' ? `${height}px` : height),
    aspectRatio: height === 'auto' ? (placement.includes('sidebar') ? '16/9' : 'auto') : 'auto',
  };

  if (!isVisible) {
    return (
      <div
        aria-hidden="true"
        style={frameStyle}
      />
    );
  }

  // Loading state
  if (loading) {
    return (
      <div
        className="animate-pulse bg-gray-100 dark:bg-[#131a45] rounded-2xl flex items-center justify-center overflow-hidden"
        style={{
          ...frameStyle,
          height: height === 'auto' ? 140 : frameStyle.height,
          border: '1px solid rgba(0,0,0,0.05)',
        }}
      >
        <div className="w-full h-full bg-gradient-to-r from-transparent via-white/5 to-transparent skew-x-12 translate-x-[-100%] animate-[shimmer_2s_infinite]" />
      </div>
    );
  }

  // Error or no ads
  if (error || ads.length === 0) {
    return null; // Gracefully handle empty state
  }

  const currentAd = ads[Math.min(currentIndex, ads.length - 1)];
  const isInternalLink = currentAd.click_url?.startsWith('/');

  return (
    <div
      className="relative overflow-hidden transition-all duration-300 group"
      style={{
        ...frameStyle,
        borderRadius: '16px',
        backgroundColor: 'rgba(0,0,0,0.02)',
      }}
    >
      <a
        href={currentAd.click_url || '#'}
        target={isInternalLink ? '_self' : '_blank'}
        rel={isInternalLink ? undefined : 'noopener noreferrer'}
        onClick={handleClick}
        className="block w-full h-full cursor-pointer no-underline group notranslate"
        translate="no"
      >
        {currentAd.campaign_type === 'video' && currentAd.video_url ? (
          <HLSVideoPlayer
            hlsUrl={getMediaUrl(currentAd.hls_url) || undefined}
            fallbackUrl={getMediaUrl(currentAd.video_url) || ''}
            poster={getMediaUrl(currentAd.thumbnail_url) || undefined}
            className="w-full h-full block transition-transform duration-700 group-hover:scale-105"
            controls={false}
            autoPlay={isVisible}
            muted
            loop
            compact
          />
        ) : (
          <img
            src={getMediaUrl(currentAd.image_url) || '/placeholder-ad.jpg'}
            alt={currentAd.title || 'Advertisement'}
            className="w-full h-full object-cover block transition-transform duration-700 group-hover:scale-105"
            loading="eager"
            decoding="async"
          />
        )}

        {/* Always-visible title/description overlay (readable from the start) */}
        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/85 via-black/55 to-transparent p-3 sm:p-4 pointer-events-none">
          <p className="text-white font-bold text-base sm:text-lg leading-tight line-clamp-2 drop-shadow-lg notranslate" translate="no">
            {currentAd.title}
          </p>
          <p className="text-white/90 text-xs sm:text-sm mt-1 line-clamp-2 drop-shadow notranslate" translate="no">
            Click to learn more
          </p>
        </div>

        <div className="absolute top-2 right-2 bg-black/70 backdrop-blur-md text-[10px] sm:text-[11px] font-extrabold text-white px-2.5 py-1 rounded-full uppercase tracking-widest shadow-lg border border-white/10 pointer-events-none notranslate" translate="no">
          Promoted
        </div>
      </a>
    </div>
  );
};

export default BannerDisplay;
